import {
  Box, Typography, Paper, Chip, Alert, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { isTerminalStatus } from './positionStatus';

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

const toMillis = (ts) => {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate().getTime();
  if (ts._seconds) return ts._seconds * 1000;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
};

// Estadísticas del proceso ATS, calculadas EN VIVO desde position + applications
// (stageHistory / phaseHistory / statusHistory). No se genera snapshot al cerrar:
// si el proceso se reabre, el reloj se reanuda y todo sigue reflejando la realidad.
const AtsPositionStatsTab = ({ position, applications = [] }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'en' ? 'en-US' : 'es-CL';

  const formatDuration = (ms) => {
    if (ms == null || ms < 0) return '—';
    const days = Math.floor(ms / DAY_MS);
    const hours = Math.floor((ms % DAY_MS) / HOUR_MS);
    if (days > 0) return `${days} d ${hours} h`;
    if (hours > 0) return `${hours} h`;
    return `${Math.max(1, Math.round(ms / (60 * 1000)))} min`;
  };

  const formatDate = (ms) => {
    if (ms == null) return '—';
    return new Date(ms).toLocaleDateString(locale);
  };

  // ── Estructura del pipeline ───────────────────────────────────────────────
  const phases = position?.pipeline?.phases || [];
  const allStages = [];
  phases.forEach((phase) => {
    (phase.stages || []).forEach((stage) => {
      allStages.push({ ...stage, phaseId: phase.id, phaseName: phase.name });
    });
  });
  const firstStageId = allStages[0]?.id || null;

  // ── Tiempo total del proceso ──────────────────────────────────────────────
  const processStart = toMillis(position?.created);
  // Cerrado y Cancelado son terminales: ambos dan por finalizado el proceso
  const isClosed = isTerminalStatus(position?.status);

  // Fecha de cierre: última entrada de statusHistory con destino terminal;
  // fallback a `modified` para procesos cerrados antes de existir statusHistory
  const closeEntries = (position?.statusHistory || [])
    .filter((s) => isTerminalStatus(s.to))
    .map((s) => toMillis(s.changedAt))
    .filter((ms) => ms != null)
    .sort((a, b) => a - b);
  const closeMs = closeEntries.length > 0 ? closeEntries[closeEntries.length - 1] : null;
  const closeIsApprox = isClosed && closeMs == null;
  const processEnd = isClosed ? (closeMs ?? toMillis(position?.modified) ?? Date.now()) : Date.now();
  const totalMs = processStart != null ? processEnd - processStart : null;

  // ── Tiempo por fase (phaseHistory) ────────────────────────────────────────
  const phaseDurations = {};
  if (processStart != null && phases.length > 0) {
    const events = [...(position?.phaseHistory || [])]
      .map((e) => ({ ...e, ms: toMillis(e.changedAt) }))
      .filter((e) => e.ms != null)
      .sort((a, b) => a.ms - b.ms);
    let currentPhaseId = phases[0].id;
    let t0 = processStart;
    events.forEach((e) => {
      const phaseId = e.fromPhaseId || currentPhaseId;
      phaseDurations[phaseId] = (phaseDurations[phaseId] || 0) + Math.max(0, e.ms - t0);
      currentPhaseId = e.toPhaseId || currentPhaseId;
      t0 = e.ms;
    });
    phaseDurations[currentPhaseId] = (phaseDurations[currentPhaseId] || 0) + Math.max(0, processEnd - t0);
  }

  // ── Candidatos: totales ───────────────────────────────────────────────────
  const discardedApps = applications.filter((a) => a.status === 'discarded');
  const blockedApps = applications.filter((a) => a.status === 'blocked');
  const activeApps = applications.filter((a) => a.status !== 'discarded' && a.status !== 'blocked');
  const hiredStageIds = allStages.filter((s) => s.type === 'hired').map((s) => s.id);
  const hiredCount = activeApps.filter((a) => hiredStageIds.includes(a.currentStageId)).length;

  // ── Por etapa: actuales, pasaron por, descartados, tiempo promedio ───────
  // Se reconstruyen los intervalos de permanencia de cada candidato desde su
  // stageHistory: entra a la primera etapa al postular (createdAt) y cada
  // movimiento cierra el intervalo anterior. Para quien sigue en una etapa el
  // intervalo corre hasta ahora (o hasta el cierre del proceso). Descartados y
  // bloqueados no suman intervalo en curso (no hay timestamp de descarte),
  // pero sí cuentan en "pasaron por".
  const stageStats = {};
  allStages.forEach((s) => {
    stageStats[s.id] = { current: 0, passed: new Set(), discarded: 0, durations: [] };
  });

  activeApps.forEach((a) => {
    if (stageStats[a.currentStageId]) stageStats[a.currentStageId].current += 1;
  });
  discardedApps.forEach((a) => {
    if (stageStats[a.currentStageId]) stageStats[a.currentStageId].discarded += 1;
  });

  applications.forEach((app) => {
    const appId = app.uid || app.id;
    const history = [...(app.stageHistory || [])]
      .map((h) => ({ ...h, ms: toMillis(h.changedAt) }))
      .filter((h) => h.ms != null)
      .sort((a, b) => a.ms - b.ms);

    let currentStage = history.length > 0
      ? (history[0].fromStageId || firstStageId)
      : app.currentStageId;
    let t0 = toMillis(app.createdAt) ?? processStart;
    if (!currentStage || t0 == null) return;

    history.forEach((h) => {
      if (stageStats[currentStage]) {
        stageStats[currentStage].passed.add(appId);
        stageStats[currentStage].durations.push(Math.max(0, h.ms - t0));
      }
      currentStage = h.toStageId || currentStage;
      t0 = h.ms;
    });

    // Intervalo en curso (solo candidatos activos)
    if (stageStats[currentStage]) {
      stageStats[currentStage].passed.add(appId);
      if (app.status !== 'discarded' && app.status !== 'blocked') {
        stageStats[currentStage].durations.push(Math.max(0, processEnd - t0));
      }
    }
  });

  const avgMs = (durations) =>
    durations.length > 0 ? durations.reduce((acc, d) => acc + d, 0) / durations.length : null;

  const summaryCards = [
    { label: t('ats.stats.candidatesTotal'), value: applications.length, color: '#6A5AF9' },
    { label: t('ats.stats.active'), value: activeApps.length, color: '#0EA5E9' },
    { label: t('ats.stats.discarded'), value: discardedApps.length, color: '#EF4444' },
    { label: t('ats.stats.blocked'), value: blockedApps.length, color: '#374151' },
    { label: t('ats.stats.hired'), value: hiredCount, color: '#10B981' },
  ];

  if (phases.length === 0) {
    return <Alert severity="info">{t('ats.stats.noPipeline')}</Alert>;
  }

  return (
    <Box>

      {/* Cards resumen */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Paper elevation={0} sx={{ p: 2, border: '1px solid #E5E7EB', borderRadius: 2, minWidth: 220 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {t('ats.stats.totalTime')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h5" fontWeight={700}>{formatDuration(totalMs)}</Typography>
            <Chip
              label={isClosed ? t('ats.stats.closed') : t('ats.stats.inProgress')}
              size="small"
              color={isClosed ? 'default' : 'success'}
              sx={{ height: 20, fontSize: '0.65rem' }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {t('ats.stats.startedOn')}: {formatDate(processStart)}
            {isClosed && (
              <> — {t('ats.stats.closedOn')}: {formatDate(processEnd)}{closeIsApprox ? ` ${t('ats.stats.approx')}` : ''}</>
            )}
          </Typography>
        </Paper>

        {summaryCards.map((card) => (
          <Paper key={card.label} elevation={0} sx={{ p: 2, border: '1px solid #E5E7EB', borderRadius: 2, minWidth: 110 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {card.label}
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ color: card.color }}>
              {card.value}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* Tiempo por fase */}
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {t('ats.stats.phaseTimes')}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        {phases.map((phase) => (
          <Paper key={phase.id} elevation={0} sx={{ p: 1.5, border: '1px solid #E5E7EB', borderRadius: 2, minWidth: 140 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {phase.name}
            </Typography>
            <Typography variant="body1" fontWeight={700}>
              {phaseDurations[phase.id] != null ? formatDuration(phaseDurations[phase.id]) : '—'}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* Tabla por etapa */}
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {t('ats.stats.stageTable')}
      </Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
              <TableCell sx={{ fontWeight: 600 }}>{t('ats.stats.thStage')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('ats.stats.thPhase')}</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>{t('ats.stats.thCurrent')}</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>{t('ats.stats.thPassed')}</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>{t('ats.stats.thDiscarded')}</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>{t('ats.stats.thAvgTime')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allStages.map((stage) => {
              const stats = stageStats[stage.id];
              return (
                <TableRow key={stage.id} hover>
                  <TableCell><Typography variant="caption" fontWeight={600}>{stage.name}</Typography></TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{stage.phaseName}</Typography></TableCell>
                  <TableCell align="center"><Typography variant="caption">{stats.current}</Typography></TableCell>
                  <TableCell align="center"><Typography variant="caption">{stats.passed.size}</Typography></TableCell>
                  <TableCell align="center">
                    <Typography variant="caption" color={stats.discarded > 0 ? 'error' : 'inherit'}>
                      {stats.discarded}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="caption">{formatDuration(avgMs(stats.durations))}</Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
        {t('ats.stats.liveNote')}
      </Typography>

    </Box>
  );
};

export default AtsPositionStatsTab;
